// src/api/billingApi.ts
// Adaptador de PAGOS / SUSCRIPCIÓN / NOTIFICACIONES contra lealify-base (Inc13 F13.6).
// Reemplaza Firebase (payments, users.subscription, businesses.isPro, system/payment_settings,
// notifications) + las Cloud Functions BTCPay/MercadoPago + esapi (AdminSubscriptionService).
//
// Traduce el modelo TENANT-céntrico de la base ↔ el modelo USER-céntrico que el manager hereda
// de Firebase: en la base la suscripción es por tenant; el manager la trata por "usuario/negocio".
// El `uid` que ven las pantallas admin = el tenant_id de la base (clave de edición).
import { baseApi } from './baseClient';
import type { Subscription, PlanType } from '../types/user';
import type { Notification, NotificationFormData } from '../features/admin/types/notification';
import { getDaysRemaining, isExpired, isExpiringSoon } from '../utils/subscriptionDates';

// ── helpers de mapeo ─────────────────────────────────────────────────────────
const PLAN_NAMES: Record<string, string> = {
  pro_yearly: 'Plan PRO (Anual)', pro_semiannual: 'Plan PRO (Semestral)',
  pro_monthly: 'Plan PRO (Mensual)', free: 'Plan Gratuito' };
const planNameOf = (code?: string | null) => (code && PLAN_NAMES[code]) || 'Plan PRO';
const toDate = (v: unknown): Date | null => (v ? new Date(v as string) : null);
// El manager solo distingue gratis/pago: la PERIODICIDAD viaja aparte en `planCode`
// (13-ago-2026, llegada de `pro_monthly`), para no meter un plan nuevo en PlanType.
const planFromCode = (code?: string | null): PlanType => (!code || code === 'free' ? 'free' : 'pro');
// 'semiannual' (25-ago-2026) entró para el cobro "6 y 6" de Marrieto; ver lib/pricing.js.
export type BillingPeriod = 'monthly' | 'semiannual' | 'yearly';
/** Meses que cubre cada periodicidad. Espejo de backend lib/pricing.js PLAN_MONTHS. */
export const MONTHS_BY_PERIOD: Record<BillingPeriod, number> = { monthly: 1, semiannual: 6, yearly: 12 };
const CODE_BY_PERIOD: Record<BillingPeriod, string> = {
  monthly: 'pro_monthly', semiannual: 'pro_semiannual', yearly: 'pro_yearly' };
export const periodFromCode = (code?: string | null): BillingPeriod =>
  (code === 'pro_monthly' ? 'monthly' : code === 'pro_semiannual' ? 'semiannual' : 'yearly');
export const codeFromPeriod = (plan: PlanType, period?: BillingPeriod): string =>
  (plan === 'free' ? 'free' : (CODE_BY_PERIOD[period || 'yearly'] || 'pro_yearly'));
/** Sufijo y frase de cobro a partir de los MESES del plan (no del código): un plan nuevo
 *  no vuelve a caer en el "/año" por descarte. */
export const periodSuffixOf = (months?: number | null): string =>
  (months === 1 ? '/mes' : months === 6 ? '/semestre' : '/año');
/** "al mes" / "al semestre" / "al año" — para frases del tipo "Pagas $X ___". */
export const perPeriodOf = (months?: number | null): string =>
  (months === 1 ? 'al mes' : months === 6 ? 'al semestre' : 'al año');
/** "mensual" / "semestral" / "anual" — adjetivo suelto (avisos del panel admin). */
export const periodAdjOf = (months?: number | null): string =>
  (months === 1 ? 'mensual' : months === 6 ? 'semestral' : 'anual');
/** Nombre del plan a partir de los MESES del periodo (para pantallas que solo tienen el monto). */
export const planNameByMonths = (months?: number | null): string =>
  PLAN_NAMES[CODE_BY_PERIOD[months === 1 ? 'monthly' : months === 6 ? 'semiannual' : 'yearly']] || 'Plan PRO';
export const chargedEveryOf = (months?: number | null): string =>
  (months === 1 ? 'Se cobra cada mes'
    : months === 6 ? 'Se cobra cada 6 meses'
    : 'Se cobra una vez al año');
// base status (active|trial|expired|cancelled|suspended) → status del manager.
function mapStatus(s?: string | null): Subscription['status'] {
  if (s === 'active' || s === 'trial') return 'active';
  if (s === 'cancelled') return 'cancelled';
  return 'expired'; // suspended | expired | null
}
function parseJson(v: unknown): Record<string, unknown> {
  if (!v) return {};
  if (typeof v === 'string') { try { return JSON.parse(v); } catch { return {}; } }
  return v as Record<string, unknown>;
}

// ── tipos de retorno (calzan con lo que las pantallas ya consumen) ───────────
export interface OwnerLastPayment {
  id: string; status: 'pending' | 'completed' | 'failed'; amount: number; currency: string;
  createdAt: Date; planName: string; method?: string; checkoutLink?: string;
}
export interface OwnerAccount {
  subscription: Subscription & { isTrial?: boolean };
  lastPayment: OwnerLastPayment | null;
  maxBranches: number;
  isTrial: boolean;
  /** Lo que el negocio paga por periodo, tal como lo cobra el servidor. */
  price: number;
  currency: string;
  /** Meses que cubre el periodo: 12 = anual, 1 = mensual. 0 = plan gratis. */
  planMonths: number;
  /** true = precio negociado con este cliente (no la tarifa por sucursales). */
  priceIsCustom: boolean;
  planCode: string;
  /** Qué incluye el plan, por vertical — fuente única en el servidor (lib/planFeatures.js). */
  planFeatures?: string[];
  /** Versión rica (ícono+título+desc) de los beneficios; ausente → lista plana. */
  planFeaturesRich?: PlanFeatureRich[];
  /** ¿Vence pronto? (umbral del cron 5/7 días). null = nada que avisar. */
  planDueSoon?: PlanDueSoon | null;
  /** Días de vigencia restantes según el servidor. */
  daysLeft?: number | null;
  /** Historial completo (completed/pending) para la tabla "Mis pagos". */
  paymentsAll?: { id: string; amount: number; currency: string; method: string; status: string; createdAt: Date | null; periodEnd: Date | null }[];
  /** Periodos que puede elegir (mensual/semestral/anual) con su monto y si están permitidos ahora. */
  planOptions: PlanOption[];
  /** Estado de la domiciliación (null = nunca la activó). */
  autopayStatus: AutopayStatus | null;
  /** Plan de pago VIGENTE (status active crudo + vence en el futuro). Con él solo se domicilia ese
   *  mismo periodo (candado del servidor mercadopago.autopayPlanCheck, 23-oct-2026). */
  planVigente: boolean;
}
export interface BankAccountInfo {
  bank: string; accountName: string; accountNumber: string; clabe: string;
  beneficiaryId?: string; bankBranch?: string;
}
export interface AdminPayment {
  id: string; userId: string; userName: string; businessId: string | null; businessName: string | null;
  amount: number; currency: string; method: string; status: 'pending' | 'completed' | 'failed' | 'cancelled' | 'refunded';
  createdAt: Date | null; updatedAt: Date | null;
  metadata: Record<string, unknown>;
  planData: { planId: string; planName: string; periodStart: Date | null; periodEnd: Date | null };
}
export interface AdminSubscriptionRow {
  uid: string;                 // = tenant_id (clave de edición)
  email: string; displayName: string; businessName: string | null; businessId: string | null;
  vertical: string;            // de tenants.vertical (default 'lealtad')
  subscription: { plan: PlanType; startDate: Date | null; endDate: Date | null; status: Subscription['status']; paymentMethod: string | null };
  isTrial: boolean; maxBranches: number; createdAt: Date | null;
  /** Precio ACORDADO con el cliente (subscriptions.custom_price). null = tarifa por sucursales. */
  customPrice: number | null;
  /** Código real del plan ('pro_yearly' | 'pro_monthly' | 'free') — de aquí sale la periodicidad. */
  planCode: string;
  /** Demo/QA de la casa (backend lib/demoTenants.js): oculto por defecto en Suscripciones. */
  isDemo: boolean;
  /** Foto del dueño (users.avatar_url: propia o de WhatsApp), si la tenemos. */
  avatarUrl?: string | null;
  /** Negocio archivado (tenants.status='archived'): fuera por defecto, como los demos (18-oct-2026). */
  isArchived: boolean;
}
export interface UpdateSubscriptionInput {
  plan: PlanType; endDate: Date | null; status?: string; paymentMethod?: string | null; isTrial?: boolean; maxBranches?: number;
  /** Precio acordado: número = fijarlo · null = borrarlo (vuelve a tarifa por sucursales). */
  customPrice?: number | null;
  /** Periodicidad del cobro. Ausente = anual (comportamiento previo a 13-ago-2026). */
  billingPeriod?: BillingPeriod;
}
export interface SubscriptionAlerts {
  expired: { count: number; users: Array<{ uid: string; email: string; displayName: string; businessName: string | null; endDate: Date; daysRemaining: number }> };
  expiringSoon: { count: number; users: Array<{ uid: string; email: string; displayName: string; businessName: string | null; endDate: Date; daysRemaining: number }> };
}

// ── respuestas crudas de la base ─────────────────────────────────────────────
interface BaseSub {
  tenant_id: number; plan_code: string; status: string; started_at: string | null; expires_at: string | null;
  payment_method: string | null; is_trial: number; max_branches: number; is_pro?: boolean; days_left?: number | null;
  // Precio REAL de un periodo, ya resuelto por el servidor (acordado si lo tiene, si no la
  // tarifa por sucursales). El front NO debe recalcularlo: si lo hace, enseña una cifra y el
  // cobro genera otra — justo lo que pasaba con los clientes de precio acordado.
  price?: number; currency?: string; price_is_custom?: boolean; plan_months?: number;
  plan_features?: string[];
  plan_features_rich?: PlanFeatureRich[] | null;
  plan_due_soon?: PlanDueSoon | null;
  // Periodos que el dueño puede elegir y a qué precio (27-sep-2026), con las reglas ya
  // resueltas por el servidor: PLAN_LOCKED (precio acordado) / AT_RENEWAL (acortar periodo).
  plan_options?: PlanOption[];
  // Domiciliación (Suscripciones MP, 30-ago-2026): estado del preapproval.
  autopay_status?: AutopayStatus | null;
  preapproval_id?: string | null;
}

export type AutopayStatus = 'pending' | 'authorized' | 'paused' | 'cancelled';
export interface PlanOption {
  code: string; months: number; label: string; amount: number; currency: string;
  current: boolean; allowed: boolean; reason: 'PLAN_LOCKED' | 'AT_RENEWAL' | null;
}
export interface PlanFeatureRich { icon: string; title: string; desc: string }
export interface PlanDueSoon { days_left: number; expires_at: string; is_trial: boolean }
interface BaseOwnerPayment {
  id: number; plan_code: string; amount: string | number; currency: string; method: string; status: string;
  receipt_url: string | null; checkout_url: string | null; period_start: string | null; period_end: string | null; created_at: string;
}

// ═══════════════════════════ DUEÑO (owner) ═══════════════════════════════════
export const billingApi = {
  // Estado de cuenta: suscripción real + último pago. Reemplaza el subscription sintético.
  getAccount: async (): Promise<OwnerAccount> => {
    const r = await baseApi.get<{ subscription: BaseSub; payments: BaseOwnerPayment[] }>('/billing/subscription');
    const s = r.subscription;
    const subscription: Subscription & { isTrial?: boolean } = {
      plan: planFromCode(s.plan_code),
      status: mapStatus(s.status),
      startDate: toDate(s.started_at),
      endDate: toDate(s.expires_at),
      paymentMethod: s.payment_method ?? null,
      isTrial: !!s.is_trial,
    };
    const p = r.payments && r.payments[0];
    const lastPayment: OwnerLastPayment | null = p ? {
      id: String(p.id), status: p.status as OwnerLastPayment['status'], amount: Number(p.amount),
      currency: p.currency, createdAt: toDate(p.created_at) as Date, planName: planNameOf(p.plan_code),
      method: p.method, checkoutLink: p.checkout_url || undefined,
    } : null;
    return {
      subscription, lastPayment, maxBranches: Number(s.max_branches ?? 1), isTrial: !!s.is_trial,
      price: Number(s.price ?? 0), currency: s.currency || 'MXN',
      planMonths: Number(s.plan_months ?? 12), priceIsCustom: !!s.price_is_custom,
      planCode: String(s.plan_code || 'free'),
      planFeatures: Array.isArray(s.plan_features) && s.plan_features.length ? s.plan_features : undefined,
      planFeaturesRich: Array.isArray(s.plan_features_rich) && s.plan_features_rich.length ? s.plan_features_rich : undefined,
      planDueSoon: s.plan_due_soon || null,
      daysLeft: s.days_left != null ? Number(s.days_left) : null,
      paymentsAll: (r.payments || [])
        .filter((p) => p.status === 'completed' || p.status === 'pending')
        .map((p) => ({ id: String(p.id), amount: Number(p.amount), currency: p.currency || 'MXN',
          method: p.method, status: p.status, createdAt: toDate(p.created_at), periodEnd: toDate(p.period_end) })),
      planOptions: Array.isArray(s.plan_options) ? s.plan_options.map((o) => ({ ...o, amount: Number(o.amount) })) : [],
      autopayStatus: s.autopay_status || null,
      // `subscription.status` ya viene mapeado (trial→active): aquí se usa el status CRUDO.
      planVigente: s.status === 'active' && String(s.plan_code || 'free') !== 'free'
        && !!s.expires_at && new Date(s.expires_at).getTime() > Date.now(),
    };
  },

  // Métodos disponibles (override del admin) + datos bancarios para transferencia.
  getPaymentMethods: async (): Promise<{ methods: Record<string, boolean>; bankAccount: BankAccountInfo | null }> => {
    const r = await baseApi.get<{ methods: Record<string, boolean>; bank_account: BankAccountInfo | null }>('/billing/payment-methods');
    return { methods: r.methods || { manual: true }, bankAccount: r.bank_account || null };
  },

  // `plan_code` (27-sep-2026) = el periodo que el dueño ELIGIÓ (mensual/semestral/anual). El
  // servidor lo valida (precio acordado, acortar periodo) y cobra ESE plan — antes ignoraba el
  // body y cobraba el plan vigente, que en gratis era $0 y rechazaba el pago.
  processManual: async (data: { receiptImageUrl: string; email?: string; whatsapp?: string; planCode?: string | null }): Promise<string> => {
    const r = await baseApi.post<{ payment: { id: number } }>('/billing/payments/manual',
      { receipt_url: data.receiptImageUrl, email: data.email ?? null, whatsapp: data.whatsapp ?? null, plan_code: data.planCode ?? null });
    return String(r.payment.id);
  },
  processBtcpay: async (planId = 'pro_yearly'): Promise<{ paymentId: string; checkoutLink: string; invoiceId: string }> => {
    const r = await baseApi.post<{ payment_id: number; checkout_url: string }>('/billing/payments/btcpay', { plan_code: planId });
    return { paymentId: String(r.payment_id), checkoutLink: r.checkout_url, invoiceId: '' };
  },
  processMercadoPago: async (planId = 'pro_yearly'): Promise<{ paymentId: string; initPoint: string; preferenceId: string }> => {
    const r = await baseApi.post<{ payment_id: number; checkout_url: string }>('/billing/payments/mercadopago',
      { plan_code: planId, return_path: '/dashboard/subscription' });
    return { paymentId: String(r.payment_id), initPoint: r.checkout_url, preferenceId: '' };
  },
  // DOMICILIACIÓN: el dueño autoriza su tarjeta UNA vez y el plan se cobra solo cada ciclo
  // (suscripción de Mercado Pago). El primer cargo respeta la vigencia ya pagada.
  // payerEmail (17-oct-2026): correo de la cuenta de MERCADO PAGO del dueño — MP amarra la
  // suscripción a esa cuenta. Sin él, el backend usa el de la cuenta de Lealify.
  startAutopay: async (planId?: string | null, payerEmail?: string | null): Promise<{ checkoutUrl: string; amount: number; months: number; firstCharge: string | null }> => {
    const r = await baseApi.post<{ checkout_url: string; amount: number; months: number; first_charge: string | null }>('/billing/autopay/start',
      { plan_code: planId ?? null, return_path: '/dashboard/subscription', ...(payerEmail ? { payer_email: payerEmail } : {}) });
    return { checkoutUrl: r.checkout_url, amount: Number(r.amount), months: Number(r.months), firstCharge: r.first_charge };
  },
  /** Apaga la domiciliación. El plan vigente NO se toca: solo deja de auto-cobrarse. */
  cancelAutopay: async (): Promise<void> => { await baseApi.post('/billing/autopay/cancel', {}); },

  // ═══════════════════════════ ADMIN (super_admin) ═══════════════════════════
  adminListPayments: async (): Promise<AdminPayment[]> => {
    const r = await baseApi.get<{ payments: Array<Record<string, unknown>> }>('/admin/payments');
    return (r.payments || []).map((p): AdminPayment => {
      const meta = parseJson(p.metadata);
      if (p.receipt_url) meta.receiptUrl = p.receipt_url;
      return {
        id: String(p.id), userId: String(p.tenant_id), userName: String(p.tenant_name || ''),
        businessId: (p.tenant_slug as string) || null, businessName: (p.tenant_name as string) || null,
        amount: Number(p.amount), currency: String(p.currency || 'MXN'),
        method: String(p.method), status: p.status as AdminPayment['status'],
        createdAt: toDate(p.created_at), updatedAt: toDate(p.approved_at || p.created_at),
        metadata: meta,
        planData: { planId: String(p.plan_code || 'pro_yearly'), planName: planNameOf(p.plan_code as string),
          periodStart: toDate(p.period_start), periodEnd: toDate(p.period_end) },
      };
    });
  },
  approvePayment: async (id: string): Promise<void> => { await baseApi.post(`/admin/payments/${id}/approve`); },
  rejectPayment: async (id: string): Promise<void> => { await baseApi.post(`/admin/payments/${id}/reject`); },

  adminListSubscriptions: async (): Promise<AdminSubscriptionRow[]> => {
    const r = await baseApi.get<{ subscriptions: Array<Record<string, unknown>> }>('/admin/subscriptions');
    return (r.subscriptions || []).map((row): AdminSubscriptionRow => ({
      uid: String(row.id),
      email: String(row.owner_email || ''), displayName: String(row.owner_name || row.name || ''),
      businessName: (row.name as string) || null, businessId: (row.slug as string) || null,
      vertical: (row.vertical as string) || 'lealtad',
      subscription: {
        plan: planFromCode(row.plan_code as string),
        startDate: toDate(row.started_at), endDate: toDate(row.expires_at),
        status: row.sub_status ? mapStatus(row.sub_status as string) : 'active',
        paymentMethod: (row.payment_method as string) ?? null,
      },
      isTrial: !!row.is_trial, maxBranches: Number(row.max_branches ?? 1), createdAt: toDate(row.tenant_created),
      customPrice: row.custom_price == null ? null : Number(row.custom_price),
      planCode: String(row.plan_code || 'free'),
      isDemo: !!Number(row.is_demo),
      isArchived: row.tenant_status === 'archived',
      avatarUrl: (row.owner_avatar as string) || null,
    }));
  },
  // Edita la suscripción de un tenant (uid = tenant_id). Reemplaza la runTransaction Firebase.
  updateSubscription: async (tenantId: string, input: UpdateSubscriptionInput): Promise<void> => {
    await baseApi.put(`/admin/tenants/${tenantId}/subscription`, {
      action: 'set',
      planCode: codeFromPeriod(input.plan, input.billingPeriod),
      status: input.status || 'active',
      expiresAt: input.endDate ? new Date(input.endDate).toISOString() : null,
      maxBranches: input.maxBranches,
      paymentMethod: input.paymentMethod ?? null,
      isTrial: !!input.isTrial,
      customPrice: input.customPrice ?? null,
    });
  },
  // Alertas de vencimiento (derivadas de la lista, sin endpoint extra).
  // ⚠️ La cuenta se hace por DÍAS con los helpers de utils/subscriptionDates — los mismos que usa
  // la tabla de suscripciones a la que lleva el botón «Revisar». Antes aquí se comparaba con la
  // hora exacta (`end < now`) y la tabla redondeaba a días: el día del corte el aviso decía
  // «1 vencida» y la lista salía vacía. Si tocas esto, tócalo allá también — o mejor, no lo dupliques.
  subscriptionAlerts: async (): Promise<SubscriptionAlerts> => {
    const rows = await billingApi.adminListSubscriptions();
    const exp: SubscriptionAlerts['expired']['users'] = [], soon: SubscriptionAlerts['expiringSoon']['users'] = [];
    for (const r of rows) {
      if (r.subscription.plan === 'free' || !r.subscription.endDate) continue;
      const daysRemaining = getDaysRemaining(r.subscription.endDate) ?? 0;
      const entry = { uid: r.uid, email: r.email, displayName: r.displayName, businessName: r.businessName, endDate: r.subscription.endDate, daysRemaining };
      if (isExpired(r.subscription.endDate)) exp.push(entry);
      else if (isExpiringSoon(r.subscription.endDate)) soon.push(entry);
    }
    return { expired: { count: exp.length, users: exp }, expiringSoon: { count: soon.length, users: soon } };
  },

  getPaymentSettings: async (): Promise<{ bankAccount: BankAccountInfo | null; methods: Record<string, boolean> }> => {
    const r = await baseApi.get<{ bank_account: BankAccountInfo | null; payment_methods: Record<string, boolean> }>('/admin/payment-settings');
    return { bankAccount: r.bank_account || null, methods: r.payment_methods || { manual: true } };
  },
  savePaymentSettings: async (patch: { bankAccount?: BankAccountInfo; methods?: Record<string, boolean> }): Promise<void> => {
    await baseApi.put('/admin/payment-settings', { bank_account: patch.bankAccount, payment_methods: patch.methods });
  },
  getBillingSettings: () => baseApi.get<{ grace_days: number; remind_days: number }>('/admin/billing-settings'),
  saveBillingSettings: (cfg: { grace_days?: number; remind_days?: number }) => baseApi.put<{ ok: true }>('/admin/billing-settings', cfg),

  // ═══════════════════════════ NOTIFICACIONES ════════════════════════════════
  // targetType del manager (all|vertical|specific) ↔ base (all|vertical|tenant).
  adminListNotifications: async (): Promise<Notification[]> => {
    const r = await baseApi.get<{ notifications: Array<Record<string, unknown>> }>('/admin/notifications');
    return (r.notifications || []).map(mapNotifFromBase);
  },
  createNotification: (form: NotificationFormData) => baseApi.post<{ id: number }>('/admin/notifications', mapNotifToBase(form)),
  updateNotification: (id: string, form: NotificationFormData) => baseApi.put<{ ok: true }>(`/admin/notifications/${id}`, mapNotifToBase(form)),
  deleteNotification: (id: string) => baseApi.del<{ ok: true }>(`/admin/notifications/${id}`),

  // panel del usuario
  myNotifications: async (): Promise<Notification[]> => {
    const r = await baseApi.get<{ notifications: Array<Record<string, unknown>> }>('/me/notifications');
    return (r.notifications || []).map(mapNotifFromBase);
  },
  dismissNotification: (id: string) => baseApi.post<{ ok: true }>(`/me/notifications/${id}/dismiss`),
};

// ── mapeo de notificaciones ──────────────────────────────────────────────────
const TARGET_TO_BASE: Record<string, string> = { all: 'all', vertical: 'vertical', specific: 'tenant' };
const TARGET_FROM_BASE: Record<string, Notification['targetType']> = { all: 'all', vertical: 'vertical', tenant: 'specific' };

function mapNotifFromBase(n: Record<string, unknown>): Notification {
  return {
    id: String(n.id), title: String(n.title || ''), message: String(n.message || ''),
    targetType: TARGET_FROM_BASE[String(n.target_type)] || 'all',
    targetValue: (n.target_value as string) || undefined,
    startDate: toDate(n.starts_at) || new Date(),
    endDate: toDate(n.ends_at),
    isDismissible: n.is_dismissible !== 0 && n.is_dismissible !== false,
    createdAt: toDate(n.created_at) || new Date(),
    updatedAt: toDate(n.updated_at) || new Date(),
    createdBy: String(n.created_by || ''),
    status: (n.status as Notification['status']) || 'draft',
  };
}
function mapNotifToBase(f: NotificationFormData): Record<string, unknown> {
  return {
    title: f.title, message: f.message,
    targetType: TARGET_TO_BASE[f.targetType] || 'all',
    targetValue: f.targetValue ?? null,
    startsAt: f.startDate ? new Date(f.startDate).toISOString() : null,
    endsAt: f.endDate ? new Date(f.endDate).toISOString() : null,
    isDismissible: f.isDismissible,
    status: f.status,
  };
}

export default billingApi;
